import React, { useState, useEffect, useCallback } from "react";
import { getAuthHeaders } from "../utils/auth";
import { useLanguage } from "../utils/i18n";

const API = "/api";

const PLAN_COLORS = {
  free_trial: "#f59e0b",
  basic:      "#4f8ef7",
  pro:        "#ff69b4",
  expired:    "#ef4444",
};

export default function ProfileModal({ profile, billingStatus, onClose, onUpdate, onGoToBilling, onSignOut }) {
  const [name,   setName]   = useState(profile?.name   || "");
  const [school, setSchool] = useState(profile?.school || "");
  const [major,  setMajor]  = useState(profile?.major  || "");
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const { t } = useLanguage();
  // Sync fields if profile prop changes (e.g. loaded after mount)
  useEffect(() => {
    setName(profile?.name   || "");
    setSchool(profile?.school || "");
    setMajor(profile?.major  || "");
  }, [profile]);

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const headers = await getAuthHeaders();
      await fetch(`${API}/profile`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ name, school, major }),
      });
      onUpdate({ name, school, major });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {}
    setSaving(false);
  }, [name, school, major, onUpdate]);

  const handlePortal = useCallback(async () => {
    setPortalLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API}/billing/portal`, { method: "POST", headers });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch {}
    setPortalLoading(false);
  }, []);

  const planColor = PLAN_COLORS[billingStatus?.plan] || "#64748b";
  const voicePct  = billingStatus?.voice_limit > 0
    ? Math.min(100, Math.round((billingStatus.voice_used / billingStatus.voice_limit) * 100))
    : 0;
  const aiPct = billingStatus?.ai_limit > 0
    ? Math.min(100, Math.round((billingStatus.ai_used / billingStatus.ai_limit) * 100))
    : 0;

  const email   = profile?.email || "";
  const initial = (name || email || "?").trim()[0].toUpperCase();

  const trialEndFormatted = React.useMemo(() => {
    if (billingStatus?.plan !== "free_trial" || !billingStatus?.trial_end) return null;
    try {
      return new Date(billingStatus.trial_end).toLocaleDateString(undefined, {
        month: "short", day: "numeric", year: "numeric",
      });
    } catch { return null; }
  }, [billingStatus]);

  const planEndFormatted = React.useMemo(() => {
    if (!billingStatus?.plan_end) return null;
    try {
      return new Date(billingStatus.plan_end).toLocaleDateString(undefined, {
        month: "short", day: "numeric", year: "numeric",
      });
    } catch { return null; }
  }, [billingStatus]);

  const isPaid = billingStatus?.plan === "default" || billingStatus?.plan === "basic" || billingStatus?.plan === "pro";

  return (
    <div className="profile-overlay" onClick={onClose}>
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="profile-modal-head">
          <div className="profile-avatar" style={{ background: planColor }}>
            {initial}
          </div>
          <div className="profile-head-info">
            <div className="profile-email">{email || "—"}</div>
          </div>
          <button className="profile-close-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* ── Usage — temporarily hidden ── */}
        {false && billingStatus && billingStatus.plan !== "expired" && (
          <div className="profile-section">
            <div className="profile-section-title">{t("thisMonthUsage")}</div>
            <div className="profile-usage-row">
              <span className="profile-usage-label">{t("voiceLabel")}</span>
              <div className="profile-bar-track">
                <div className="profile-bar-fill" style={{ width: `${voicePct}%`, background: "#4f8ef7" }} />
              </div>
              <span className="profile-usage-count">{billingStatus.voice_used} / {billingStatus.voice_limit}</span>
            </div>
            <div className="profile-usage-row">
              <span className="profile-usage-label">{t("aiLabel")}</span>
              <div className="profile-bar-track">
                <div className="profile-bar-fill" style={{ width: `${aiPct}%`, background: "#ff69b4" }} />
              </div>
              <span className="profile-usage-count">{billingStatus.ai_used} / {billingStatus.ai_limit}</span>
            </div>
          </div>
        )}

        {/* ── Manage subscription button ── */}
        {billingStatus && billingStatus.plan !== "expired" && (
          <div className="profile-section">
            {/* Subscription expiry info */}
            {isPaid && planEndFormatted && (
              <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8 }}>
                到期时间：{planEndFormatted}
              </div>
            )}
            {isPaid && !planEndFormatted && billingStatus.has_recurring_sub && (
              <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8 }}>
                自动续订中
              </div>
            )}
            {isPaid ? (
              <button
                className="profile-manage-btn"
                onClick={handlePortal}
                disabled={portalLoading}
              >
                {portalLoading ? "跳转中…" : "管理 / 取消订阅 →"}
              </button>
            ) : (
              <button
                className="profile-manage-btn"
                onClick={() => { onClose(); onGoToBilling(); }}
              >
                {t("manageSubscription")}
              </button>
            )}
          </div>
        )}

        {billingStatus?.plan === "expired" && (
          <div className="profile-section">
            <p style={{ color: "#ef4444", fontWeight: 600, marginBottom: 10 }}>{t("planExpired")}</p>
            <button className="profile-manage-btn" onClick={() => { onClose(); onGoToBilling(); }}>
              {t("renewPlan")}
            </button>
          </div>
        )}

        {/* ── Profile fields ── */}
        <div className="profile-section">
          <div className="profile-section-title">{t("profileTitle")} <span style={{ fontWeight: 400, color: "#94a3b8", fontSize: 12 }}>{t("allOptional")}</span></div>

          <label className="profile-field-label">{t("nameLabel")}</label>
          <input
            className="profile-field-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("namePlaceholder")}
            maxLength={100}
          />

          <label className="profile-field-label">{t("schoolLabel")}</label>
          <input
            className="profile-field-input"
            value={school}
            onChange={(e) => setSchool(e.target.value)}
            placeholder={t("schoolPlaceholder")}
            maxLength={100}
          />

          <label className="profile-field-label">{t("majorLabel")}</label>
          <input
            className="profile-field-input"
            value={major}
            onChange={(e) => setMajor(e.target.value)}
            placeholder={t("majorPlaceholder")}
            maxLength={100}
          />

          <button
            className="profile-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saved ? t("savedLabel") : saving ? t("savingLabel") : t("saveProfile")}
          </button>
        </div>

        {/* ── Footer ── */}
        <div className="profile-modal-footer">
          <button className="profile-signout-btn" onClick={onSignOut}>
            {t("signOut")}
          </button>
        </div>

      </div>
    </div>
  );
}
